import { TrendingDown, Bell, Clock, Brain, Plane, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BrandLockup from "@/components/layout/BrandLockup";
import { getLoginUrl } from "@/lib/auth";
import { useAuthStore } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";

const features = [
  { icon: Brain, title: "AI Trip Intelligence", desc: "Describe your trip in plain English. Coach figures out airports, timing, and logistics — like having a travel agent who never sleeps." },
  { icon: Plane, title: "Smart Airport Selection", desc: "Going to Miami for a cruise? Coach automatically checks MIA and FLL, calculates port transit times, and finds deals at both." },
  { icon: Clock, title: "Timing That Makes Sense", desc: "Cruise boards at 11 AM? Coach works backwards — deplane, bags, Uber to port — and sets the right arrival cutoff automatically." },
  { icon: MapPin, title: "Event-Aware Planning", desc: "Attending a show, game, or conference? Coach knows venue logistics and builds arrival times around your real schedule." },
  { icon: TrendingDown, title: "Around-the-Clock Fare Tracking", desc: "Your fares are re-checked several times a day, every day. When one hits a new low, you hear about it." },
  { icon: Bell, title: "Smart Alerts", desc: "Get told when a fare you are watching drops. Focus Set recommendations highlight the flights that match your preferred airline, timing, and budget." },
];

const steps = [
  { num: "1", title: "Tell Coach Your Plans", desc: "Describe your trip naturally: \"NCL cruise from Miami March 15\" or \"Vegas for a bachelor party April 5.\" Coach handles the rest." },
  { num: "2", title: "AI Builds Your Routes", desc: "Coach generates optimized routes — alternative airports, day-before options, timing constraints based on real logistics." },
  { num: "3", title: "Track & Save", desc: "Your fare is watched around the clock. Get alerted the moment it drops, and see its full price history before you book." },
];

export default function LandingPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const navigate = useNavigate();

  // Shared "Your Steward" chat orb: landing page only, never inside the logged-in app.
  useEffect(() => {
    if (document.querySelector('script[src*="steward/orb.js"]')) return;
    const s = document.createElement("script");
    s.src = "https://wtadmin.wade.technology/steward/orb.js";
    s.defer = true;
    s.dataset.accent = "#0F8C83";
    s.dataset.surface = "#FFFFFF";
    s.dataset.ink = "#1A1A19";
    document.body.appendChild(s);
  }, []);

  function handleGetStarted() {
    if (isAuthenticated) {
      navigate("/dashboard");
    } else {
      window.location.href = getLoginUrl();
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        <div className="container mx-auto px-4 py-6 max-w-7xl relative">
          <nav className="flex items-center justify-between mb-16">
            <Link to="/" aria-label="TripMuscle home">
              <BrandLockup tagline="Everyone's Fare Search Coach" />
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/pricing"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Pricing
              </Link>
              <Button onClick={handleGetStarted} size="sm">
                {isAuthenticated ? "Go to Dashboard" : "Sign In"}
              </Button>
            </div>
          </nav>

          <div className="text-center max-w-3xl mx-auto space-y-6 pb-20">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Build Your Strongest{" "}
              <span className="text-primary">Trip</span> Yet
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
              Tell your fare search coach where everyone's going. It figures out the airports, timing, and logistics — then watches the fare around the clock until you're ready to book.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" onClick={handleGetStarted}>
                {isAuthenticated ? "Go to Dashboard" : "Get Started Free"}
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}>
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Features */}
      <section id="features" className="container mx-auto px-4 py-20 max-w-7xl">
        <h2 className="text-3xl font-bold text-center mb-12">
          Everything You Need to Crush Your Travel Goals
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <Card key={f.title} className="bg-card/50 hover:bg-card transition-colors">
              <CardContent className="p-6 space-y-3">
                <f.icon className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-semibold">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="container mx-auto px-4 py-20 max-w-7xl">
        <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {steps.map((s) => (
            <div key={s.num} className="text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-accent-foreground font-bold text-xl flex items-center justify-center mx-auto">
                {s.num}
              </div>
              <h3 className="text-lg font-semibold">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-20 max-w-7xl">
        <div className="text-center space-y-6 max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold">Ready to Build Some TripMuscle?</h2>
          <p className="text-muted-foreground">
            Describe your trip. Coach builds the routes. Prices tracked automatically. It's that simple.
          </p>
          <Button size="lg" onClick={handleGetStarted}>
            {isAuthenticated ? "Go to Dashboard" : "Sign In Free"}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 max-w-7xl text-center space-y-3">
          <a href="https://wade.technology" target="_blank" rel="noopener noreferrer" className="inline-flex items-baseline hover:opacity-80 transition-opacity">
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "1.15rem", fontWeight: 800, letterSpacing: "-0.02em", color: "#0F172A" }}>
              Wade<span style={{ color: "#22C55E" }}>.</span>Technology
            </span>
          </a>
          <p className="text-sm"><a href="tel:+19726001001" className="text-muted-foreground hover:text-foreground transition-colors">Call Steward: 972-600-1001</a></p>
          <p className="text-xs text-muted-foreground/60">&copy; {new Date().getFullYear()} Wade.Technology. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
